import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../lib/hooks/use-translate'
import { SystemAdminUserInput } from '../../../lib/types/models/users/user'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ValidationButtons from '../../elements/validation-buttons'

const getFormSchema = (
  t: TFunction<AvailableLanguages, undefined>,
  isFirstNameOptional: boolean
): ZodType<SystemAdminUserInput> =>
  z.object({
    email: createStringSchema('email', { isEmail: true }),
    firstName: createStringSchema('first name', { minimumLength: isFirstNameOptional ? 0 : 1 }),
    lastName: createStringSchema('last name'),
    phoneNumber: PHONE_NUMBER_SCHEMA(t).nullish()
  })

type SystemAdminUserFormProps = BaseFormProps<SystemAdminUserInput, true> & {
  className?: string
  isFirstNameOptional?: boolean
}

const SystemAdminUserForm = ({
  className,
  defaultValues,
  isFirstNameOptional = false,
  ...validationButtonProps
}: SystemAdminUserFormProps) => {
  const { t } = useTranslate()
  const form = useForm<SystemAdminUserInput>({
    resolver: zodResolver(getFormSchema(t, isFirstNameOptional)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    required={!isFirstNameOptional}
                    type="text"
                    label="First name"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="text" label="Last name" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="email" label="Email" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledPhoneInput
                    {...field}
                    value={field.value ?? undefined}
                    label="Phone number"
                    defaultCountry="FR"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default SystemAdminUserForm
